import React, { FC, useState } from 'react';
import { pick } from 'lodash';
import { FormikProvider } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Scrollbar from 'vibo-ui/Scrollbar';
import PlaylistSongs from './PlaylistSongs';
import PlaylistHeader from './PlaylistHeader';
import Container from 'components/common/Container';
import SongIdeaPageHeader from './SongIdeaPageHeader';
import ContentArea from 'components/common/ContentArea';
import ExportSources from 'components/common/ExportSources';
import LoadingOverlay from 'components/common/LoadingOverlay';
import SongIdeasDetailsCard from 'components/songIdeas/SongIdeasDetailsCard';
import SongIdeasPlaylistHeader from 'components/headers/SongIdeasPlaylistHeader';
import { SongPlayBarContextProvider } from 'components/context/SongPlayBarContext';

import { ReactComponent as AppleMusicIcon } from 'resources/img/svg/appleMusicOriginal.svg';

import { useModal } from 'vibo-ui/Modal';
import { onError } from 'graphql/helpers';
import { toastNotify } from 'graphql/hooks/common';
import { MAX_EXPORT_SONGS_TO_APPLE_MUSIC, SONG_IDEAS_KEYS } from 'services/constants';
import { useFormikAutoSave } from 'graphql/hooks/form';
import { updateSongIdeasCache } from 'graphql/cache/songIdeas';
import { updateSongIdeasValidationSchema } from './constants';

import { GET_SONG_IDEAS_BY_ID } from 'graphql/queries/songIdeas';
import { IMPORT_PLAYLIST_TO_SONG_IDEAS } from 'graphql/mutations/songs';
import {
  ADD_SONG_TO_SONG_IDEAS,
  REMOVE_SONG_FROM_SONG_IDEAS,
  UPDATE_SONG_IDEAS,
} from 'graphql/mutations/songIdeas';

import { IconmoonFont } from 'vibo-ui/Icon';
import { Modals, MusicExportSource, MusicImportSource } from 'types/enums';
import { SongDropdownOption, SongIdeasDetailsInnerProps } from './interfaces';
import { AddSongFromLinkModalProps } from 'components/modals/AddSongFromLinkModal';
import { TypeExportEventSource } from 'components/modals/ExportEventToSourceModal';
import { AddSongsFromMusicSourceModalProps } from 'components/modals/AddSongsFromMusicSourceModal';
import { ExportPlaylistToSourceModalProps } from 'components/modals/ExportPlaylistToSourceModal';
import { AddSongsToPlaylistFromSearchModalProps } from 'components/modals/AddSongsToPlaylistFromSearchModal';

import useStyles from './style';

const SongIdeasDetailsInner: FC<SongIdeasDetailsInnerProps> = ({ songIdeas, isMySongIdeas }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const location = useLocation();
  const { id: songIdeasId } = useParams<{ id: string }>();

  const songIdeasType = location.state?.type || SONG_IDEAS_KEYS.me;

  const [songsCount, setSongsCount] = useState(0);

  const { openModal } = useModal();

  const openExportSongIdeasSpotifyModal = (source: TypeExportEventSource) =>
    songIdeas &&
    openModal<ExportPlaylistToSourceModalProps>({
      key: Modals.exportPlaylistToSource,
      props: {
        songIdeasId: songIdeas._id,
        source,
      },
    });

  const [updateSongIdeas] = useMutation<UpdateSongIdeasData>(UPDATE_SONG_IDEAS, {
    onCompleted: data => {
      const { updateSongIdeas } = data;

      songIdeasId &&
        updateSongIdeasCache(songIdeasId, {
          title: updateSongIdeas.title,
          description: updateSongIdeas.description || '',
          coverPhoto: updateSongIdeas.coverPhoto,
          syncSettings: updateSongIdeas.syncSettings,
        });

      formik.setSubmitting(false);
      toastNotify({ text: t('changesSavedSuccessfully') });
    },
    onError: error => {
      formik.setSubmitting(false);
      onError(error);
    },
  });

  const openAddSongToPlaylistFromSearchModal = () =>
    !!songIdeas &&
    openModal<AddSongsToPlaylistFromSearchModalProps>({
      key: Modals.addSongsToPlaylistFromSearch,
      props: {
        playlist: songIdeas,
        variables: {
          songIdeasId,
          //TODO DELETE AFTER BE REFACTOR PAGINATION
          limit: 50,
        },
        mutations: {
          add: ADD_SONG_TO_SONG_IDEAS,
          delete: REMOVE_SONG_FROM_SONG_IDEAS,
        },
        refetchQueries: ['getSongIdeasById', 'getSongIdeasSongs'],
      },
    });
  const openAddSongsFromMusicSourceModal = (source: MusicImportSource) =>
    openModal<AddSongsFromMusicSourceModalProps>({
      key: Modals.addSongsFromMusicSource,
      props: {
        source,
        variables: {
          songIdeasId,
        },
        importMutation: IMPORT_PLAYLIST_TO_SONG_IDEAS,
        refetchQueries: ['getSongIdeasById', 'getSongIdeasSongs'],
      },
    });
  const openAddSongFromLinkModal = () =>
    openModal<AddSongFromLinkModalProps>({
      key: Modals.addSongFromLink,
      props: {
        mutation: ADD_SONG_TO_SONG_IDEAS,
        refetchQueries: ['getSongIdeasById', 'getSongIdeasSongs'],
        variables: {
          songIdeasId,
        },
      },
    });

  const handleUpdatePlaylist = async (payload: UpdateSongIdeasPayload) => {
    isMySongIdeas &&
      updateSongIdeas({
        variables: {
          songIdeasId: songIdeas?._id,
          payload,
        },
      });
  };

  const { formik } = useFormikAutoSave<UpdateSongIdeasForm>(
    {
      initialValues: pick(songIdeas, [
        'title',
        'description',
        'syncSettings.spotify.isSynchronized',
      ]) as UpdateSongIdeasForm,
      validationSchema: updateSongIdeasValidationSchema,
      onSubmit: values => {
        handleUpdatePlaylist(values);
      },
    },
    1000,
    true
  );

  const addSongsDropdownOptions: SongDropdownOption[] = [
    {
      icon: IconmoonFont['search-16'],
      text: 'Search songs',
      onClick: openAddSongToPlaylistFromSearchModal,
    },
    {
      icon: IconmoonFont['spotifyColorful-16'],
      text: t('spotify'),
      onClick: () => openAddSongsFromMusicSourceModal(MusicImportSource.spotify),
    },
    {
      icon: AppleMusicIcon,
      text: t('appleMusic'),
      onClick: () => openAddSongsFromMusicSourceModal(MusicImportSource.appleMusic),
    },
    {
      icon: IconmoonFont['link-16'],
      text: t('songLink'),
      onClick: openAddSongFromLinkModal,
    },
  ];

  return (
    <div className={classes.songIdeasDetails}>
      <SongIdeasPlaylistHeader currentTab={songIdeasType} />
      <FormikProvider value={formik}>
        <Container>
          {!!songIdeas ? (
            <SongIdeaPageHeader songIdeas={songIdeas} currentTab={songIdeasType} />
          ) : null}
          <div className={classes.songIdeasContent}>
            {!!songIdeas ? (
              <SongIdeasDetailsCard songIdeas={songIdeas} canEdit={isMySongIdeas} />
            ) : null}
            <div className={classes.playlistContent}>
              {!!songIdeas ? (
                <ContentArea className={classes.playlistContentArea} shadowed>
                  <Scrollbar id="playlist-details-songs" shiftThumbStyle="simple">
                    <PlaylistHeader
                      addSongsDropdownOptions={addSongsDropdownOptions}
                      songsCount={songsCount}
                      isMySongIdeas={isMySongIdeas}
                      isSpotifySyncEnabled={songIdeas.syncSettings.spotify.isSynchronized}
                    />
                    <SongPlayBarContextProvider>
                      <PlaylistSongs
                        songIdeasId={songIdeasId as string}
                        isMySongIdeas={isMySongIdeas}
                        onSongsCountUpdate={setSongsCount}
                        isSpotifySyncEnabled={songIdeas.syncSettings.spotify.isSynchronized}
                      />
                    </SongPlayBarContextProvider>
                  </Scrollbar>
                </ContentArea>
              ) : null}
            </div>
          </div>
          <ExportSources
            spotify={{
              onClick: () => openExportSongIdeasSpotifyModal(MusicExportSource.spotify),
              disabled: !songIdeas?.songsCount,
            }}
            appleMusic={{
              onClick: () => openExportSongIdeasSpotifyModal(MusicExportSource.appleMusic),
              tooltipText:
                (songIdeas?.songsCount ?? 0) > MAX_EXPORT_SONGS_TO_APPLE_MUSIC
                  ? `Max ${MAX_EXPORT_SONGS_TO_APPLE_MUSIC} songs per one playlist`
                  : undefined,
              disabled:
                !songIdeas?.songsCount || songIdeas?.songsCount > MAX_EXPORT_SONGS_TO_APPLE_MUSIC,
            }}
          />
        </Container>
      </FormikProvider>
    </div>
  );
};

const SongIdeasDetails: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: songIdeasId } = useParams<{ id: string }>();

  const songIdeasType = location.state?.type || SONG_IDEAS_KEYS.me;

  const { data, loading } = useQuery<SongIdeasByIdResponse>(GET_SONG_IDEAS_BY_ID, {
    variables: {
      songIdeasId,
    },
    fetchPolicy: 'cache-and-network',
    onError: error => {
      onError(error);
      navigate(`/songIdeas/${songIdeasType}`, {
        state: location.state,
      });
    },
  });

  const songIdeas: Maybe<SongIdeas> = data?.getSongIdeasById;
  const isMySongIdeas: boolean = data?.getSongIdeasById.isOwner || false;

  return loading && !data ? (
    <LoadingOverlay />
  ) : (
    <SongIdeasDetailsInner songIdeas={songIdeas} isMySongIdeas={isMySongIdeas} />
  );
};

export default SongIdeasDetails;
